import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import {
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from 'firebase/auth';
import axios from 'axios';
import { toast } from 'react-toastify';
import i18n from '../../i18n';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

export const Profile = () => {
  const { t, i18n: i18nInstance } = useTranslation();
  const { user, userData, refreshUserData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [language, setLanguage] = useState('uk');

  useEffect(() => {
    if (userData) {
      setDisplayName(userData.displayName || '');
      setLanguage(userData.settings?.language || i18nInstance.language || 'uk');
    }
  }, [userData, i18nInstance.language]);

  const handleUpdateDisplayName = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    try {
      const token = await user.getIdToken();
      await axios.put(
        `${API_URL}/auth/profile`,
        { displayName: displayName.trim() },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      await refreshUserData();
      toast.success(t('profile.updateSuccess'));
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !user.email) return;

    if (newPassword !== confirmPassword) {
      toast.error(t('auth.confirmPassword') + ': ' + t('errors.validation'));
      return;
    }

    if (newPassword.length < 8) {
      toast.error(t('errors.validation'));
      return;
    }

    setLoading(true);
    try {
      const credential = EmailAuthProvider.credential(
        user.email,
        currentPassword
      );
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);

      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success(t('profile.passwordUpdated'));
    } catch (error: any) {
      const errorMessage =
        error.code === 'auth/wrong-password'
          ? t('errors.auth')
          : error.message || t('errors.generic');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageChange = async (newLanguage: 'uk' | 'en') => {
    if (!user) return;

    setLanguage(newLanguage);
    i18n.changeLanguage(newLanguage);

    try {
      const token = await user.getIdToken();
      await axios.put(
        `${API_URL}/auth/profile`,
        { settings: { ...userData?.settings, language: newLanguage } },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      await refreshUserData();
    } catch (error: any) {
      console.error('Failed to update language setting:', error);
      toast.error(t('errors.generic'));
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {t('errors.auth')}
          </h2>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">
          {t('profile.title')}
        </h1>
        <p className="mt-2 text-sm text-gray-600">{t('profile.settings')}</p>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">
          {t('profile.language')}
        </h2>
        <div className="flex space-x-4">
          <button
            onClick={() => handleLanguageChange('uk')}
            className={`px-4 py-2 rounded-md ${
              language === 'uk'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Українська
          </button>
          <button
            onClick={() => handleLanguageChange('en')}
            className={`px-4 py-2 rounded-md ${
              language === 'en'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            English
          </button>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">
          {t('auth.displayName')}
        </h2>
        <form onSubmit={handleUpdateDisplayName} className="space-y-4">
          <div>
            <label
              htmlFor="displayName"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {t('auth.displayName')}
            </label>
            <input
              id="displayName"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
              minLength={2}
              maxLength={50}
              className="input w-full"
              placeholder={t('auth.displayName')}
            />
            {user?.email && (
              <p className="mt-2 text-sm text-gray-500">{user.email}</p>
            )}
          </div>
          <button
            type="submit"
            disabled={loading || !displayName.trim()}
            className="btn btn-primary"
          >
            {loading ? t('common.loading') : t('common.save')}
          </button>
        </form>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">
          {t('profile.changePassword')}
        </h2>
        <form onSubmit={handleChangePassword} className="space-y-4">
          <div>
            <label
              htmlFor="currentPassword"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {t('profile.currentPassword')}
            </label>
            <input
              id="currentPassword"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              className="input w-full"
              placeholder={t('profile.currentPassword')}
            />
          </div>
          <div>
            <label
              htmlFor="newPassword"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {t('profile.newPassword')}
            </label>
            <input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              className="input w-full"
              placeholder={t('profile.newPassword')}
            />
          </div>
          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {t('auth.confirmPassword')}
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              className="input w-full"
              placeholder={t('auth.confirmPassword')}
            />
          </div>
          <button
            type="submit"
            disabled={
              loading || !currentPassword || !newPassword || !confirmPassword
            }
            className="btn btn-primary"
          >
            {loading ? t('common.loading') : t('profile.changePassword')}
          </button>
        </form>
      </div>
    </div>
  );
};
